import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Badge, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';

interface OrderLine {
  quantity: number;
  article_number: string;
  description: string;
  line_price_excl_vat: number;
}

interface Order {
  id: number;
  reference: string | null;
  description: string;
  status: string;
  supplier_status: string | null;
  supplier_order_id: string | null;
  total_amount: number;
  shipping_cost: number;
  total_amount_ex_vat: number;
  order_lines: OrderLine[];
  created_at: string;
  updated_at: string;
  supplier_updated_at: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  supplier_notes: string | null;
}

interface OrdersResponse {
  orders: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

const OrderHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const loadOrders = async (page: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        getApiUrl(`/api/web/orders?page=${page}&limit=${pagination.limit}`),
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const result = await response.json();

      if (response.ok && result.meta?.result === 'success') {
        const data = result.data as OrdersResponse;
        setOrders(data.orders);
        setPagination(data.pagination);
      } else if (response.status === 401) {
        setError(t('orders.authError'));
      } else {
        throw new Error(result.meta?.message || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err instanceof Error ? err.message : t('orders.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'primary';
      case 'processing':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return t('orders.status.new');
      case 'processing':
        return t('orders.status.processing');
      case 'shipped':
        return t('orders.status.shipped');
      case 'delivered':
        return t('orders.status.delivered');
      case 'cancelled':
        return t('orders.status.cancelled');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      loadOrders(newPage);
    }
  };

  const toggleRowExpansion = (orderId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(orderId)) {
      newExpandedRows.delete(orderId);
    } else {
      newExpandedRows.add(orderId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (isLoading) {
    return (
      <main className="container py-4">
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0">{t('common.loading')}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </main>
    );
  }

  if (error) {
    return (
      <main className="container py-4">
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Body>
                <Alert variant="danger">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </Alert>
                <Button variant="outline-primary" onClick={() => loadOrders()}>
                  <i className="fas fa-retry me-2"></i>
                  {t('common.retry')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </main>
    );
  }

  return (
    <main className="container py-4">
      <Row>
        <Col lg={12}>
          <Card className="d-flex flex-column" style={{ height: 'calc(100vh - 140px)' }}>
            <Card.Header className="flex-shrink-0 bg-dark text-white">
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                {t('orders.title')}
              </h5>
            </Card.Header>
            <Card.Body className="flex-grow-1 overflow-hidden p-0">
              {orders.length === 0 ? (
                <Alert variant="info" className="m-3 text-center">
                  <i className="fas fa-info-circle me-2"></i>
                  {t('orders.noOrders')}
                </Alert>
              ) : (
                <>
                  <div className="table-responsive" style={{ height: '100%', overflowY: 'auto' }}>
                    <Table striped hover className="mb-0 table-sm table-compact">
                      <thead className="sticky-top bg-white" style={{ top: '0', zIndex: 10 }}>
                        <tr className="table-header">
                          <th style={{ width: '80px', verticalAlign: 'top' }}>{t('orders.table.orderId')}</th>
                          <th style={{ width: '120px', verticalAlign: 'top' }}>{t('orders.table.supplierOrderId')}</th>
                          <th style={{ width: '120px', verticalAlign: 'top' }}>{t('orders.table.reference')}</th>
                          <th style={{ verticalAlign: 'top' }}>{t('orders.table.description')}</th>
                          <th style={{ width: '100px', verticalAlign: 'top' }}>{t('orders.table.amount')}</th>
                          <th style={{ width: '120px', verticalAlign: 'top' }}>{t('orders.table.status')}</th>
                          <th style={{ width: '140px', verticalAlign: 'top' }}>{t('orders.table.date')}</th>
                          <th style={{ width: '120px', verticalAlign: 'top' }}>{t('orders.table.tracking')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <React.Fragment key={order.id}>
                            <tr className="line-height-tight">
                              <td className="table-cell-bold">
                                #{order.id}
                              </td>
                              <td className="table-cell">
                                {order.supplier_order_id || '-'}
                              </td>
                              <td className="table-cell">
                                {order.reference || '-'}
                              </td>
                              <td className="table-cell">
                                {order.order_lines && order.order_lines.length > 0 ? (
                                  <>
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        toggleRowExpansion(order.id);
                                      }}
                                      style={{ color: 'inherit', cursor: 'pointer' }}
                                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                    >
                                      {order.description}
                                    </a>
                                    {' '}
                                    <button
                                      className="btn btn-link p-0"
                                      onClick={() => toggleRowExpansion(order.id)}
                                      style={{ border: 'none', background: 'none', color: '#007bff' }}
                                    >
                                      <i className={`fas fa-info-circle ${expandedRows.has(order.id) ? 'text-primary' : ''}`}></i>
                                    </button>
                                  </>
                                ) : (
                                  order.description
                                )}
                              </td>
                              <td className="table-cell-bold">
                                {formatCurrency(order.total_amount_ex_vat)}
                              </td>
                              <td className="table-cell">
                                <Badge bg={getStatusVariant(order.status)}>
                                  {getStatusText(order.status)}
                                </Badge>
                              </td>
                              <td className="table-cell">
                                <small>{formatDate(order.created_at)}</small>
                              </td>
                              <td className="table-cell">
                                {order.tracking_url && order.tracking_number ? (
                                  <a
                                    href={order.tracking_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-link p-0"
                                    title={order.tracking_number}
                                  >
                                    <i className="fas fa-external-link-alt"></i>
                                  </a>
                                ) : order.tracking_number ? (
                                  order.tracking_number
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(order.id) && order.order_lines && order.order_lines.length > 0 && (
                              <tr>
                                <td colSpan={8} className="bg-light p-3">
                                  <div className="mb-2">
                                    <strong>{t('orders.orderLines.title')}:</strong>
                                  </div>
                                  <Table size="sm" className="mb-0">
                                    <thead>
                                      <tr>
                                        <th>{t('orders.orderLines.quantity')}</th>
                                        <th>{t('orders.orderLines.articleNumber')}</th>
                                        <th>{t('orders.orderLines.description')}</th>
                                        <th className="text-end">{t('orders.orderLines.linePrice')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {order.order_lines.map((line, index) => (
                                        <tr key={index}>
                                          <td>{line.quantity}</td>
                                          <td><code>{line.article_number}</code></td>
                                          <td>{line.description}</td>
                                          <td className="text-end">{formatCurrency(line.line_price_excl_vat)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr>
                                        <td colSpan={3} className="text-end"><strong>{t('orders.orderLines.shipping')}</strong></td>
                                        <td className="text-end"><strong>{formatCurrency(order.shipping_cost)}</strong></td>
                                      </tr>
                                      <tr className="table-primary">
                                        <td colSpan={3} className="text-end"><strong>{t('orders.orderLines.total')}</strong></td>
                                        <td className="text-end"><strong>{formatCurrency(order.total_amount_ex_vat)}</strong></td>
                                      </tr>
                                    </tfoot>
                                  </Table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>

            {/* Pagination */}
            {orders.length > 0 && pagination.total_pages > 1 && (
              <Card.Footer className="flex-shrink-0 bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted">
                      {t('orders.pagination.showing', {
                        start: (pagination.page - 1) * pagination.limit + 1,
                        end: Math.min(pagination.page * pagination.limit, pagination.total),
                        total: pagination.total,
                      })}
                    </small>
                  </div>
                  <div className="btn-group">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </Button>
                    <Button variant="outline-secondary" size="sm" disabled>
                      {pagination.page} / {pagination.total_pages}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </main>
  );
};

export default OrderHistoryPage;